import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Menu, LogIn, ArrowUpRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "../../components/ui/sheet";
import { SERVICES, NAV } from "./siteData";
import { useEnquiry } from "./enquiryContext";

export function Brand() {
  return (
    <Link to="/" className="flex items-center gap-3 sm:gap-4 group shrink-0" data-testid="site-logo">
      <span className="bg-white h-12 sm:h-14 px-1.5 flex items-center shadow-sm ring-1 ring-[#C4A66A]/40 transition-transform duration-300 group-hover:-translate-y-0.5">
        <img src="/brand/vcaj-logo.png" alt="VCAJ & Associates LLP" className="h-full w-auto" data-testid="site-vcaj-logo" />
      </span>
      <span className="hidden sm:block h-9 w-px bg-[#C4A66A]/50" />
      <span className="hidden sm:block text-[10px] leading-relaxed uppercase tracking-[0.3em] text-[#C4A66A]">Chartered<br />Accountants</span>
    </Link>
  );
}

function ExpertiseMenu() {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);
  return (
    <div className="relative" onMouseEnter={() => setOpen(true)} onMouseLeave={() => setOpen(false)}>
      <button
        onClick={() => setOpen(true)}
        onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
        className={`site-link flex items-center gap-1 py-1 ${pathname.startsWith("/expertise") ? "active text-white" : ""}`}
        aria-expanded={open}
        data-testid="nav-expertise-toggle"
      >
        Verticals <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-[520px]" data-testid="nav-expertise-menu">
          <div className="bg-white text-[#14263D] shadow-2xl border-t-2 border-[#C4A66A] grid grid-cols-1">
            <Link to="/" className="px-6 py-4 border-b border-slate-100 text-xs uppercase tracking-[0.25em] text-slate-500 hover:text-[#14263D] flex items-center justify-between" data-testid="nav-expertise-overview">
              All verticals <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
            {SERVICES.map((s) => (
              <Link key={s.slug} to={`/expertise/${s.slug}`} className="px-6 py-4 flex gap-5 items-start hover:bg-[#F7F4EC] transition-colors" data-testid={`nav-service-${s.slug}`}>
                <span className="font-serif text-2xl text-[#C4A66A] leading-none w-8">{s.no}</span>
                <span>
                  <span className="block font-serif text-lg font-semibold">{s.title}</span>
                  <span className="block text-xs text-slate-500 mt-1 leading-relaxed">{s.short}</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MobileMenu({ onEnquire }) {
  const [open, setOpen] = useState(false);
  const { pathname, hash } = useLocation();
  useEffect(() => setOpen(false), [pathname, hash]);
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button className="xl:hidden text-white p-2" aria-label="Open menu" data-testid="mobile-menu-btn"><Menu className="h-6 w-6" /></button>
      </SheetTrigger>
      <SheetContent side="right" className="bg-[#14263D] text-white border-l border-white/10 w-[86vw] sm:max-w-sm overflow-y-auto" data-testid="mobile-menu">
        <SheetTitle className="sr-only">Menu</SheetTitle>
        <div className="mt-8 site-eyebrow">Verticals</div>
        <div className="mt-3 flex flex-col">
          {SERVICES.map((s) => (
            <Link key={s.slug} to={`/expertise/${s.slug}`} className="py-3 border-b border-white/10 font-serif text-xl" data-testid={`mobile-nav-service-${s.slug}`}>
              <span className="text-[#C4A66A] mr-3 text-base">{s.no}</span>{s.title}
            </Link>
          ))}
        </div>
        <div className="mt-8 flex flex-col">
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} className="py-3 border-b border-white/10 text-sm uppercase tracking-[0.2em]" data-testid={`mobile-nav-${n.id}`}>{n.label}</Link>
          ))}
        </div>
        <div className="mt-8 flex flex-col gap-3">
          <button onClick={() => { setOpen(false); onEnquire(); }} className="btn-site-gold h-12 text-sm font-semibold" data-testid="mobile-enquire-btn">Enquire now</button>
          <Link to="/portal" className="h-12 border border-[#C4A66A]/50 text-[#C4A66A] flex items-center justify-center gap-2 text-sm font-semibold" data-testid="mobile-employee-login">
            <LogIn className="h-4 w-4" /> Employee Login
          </Link>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default function SiteHeader() {
  const openEnquiry = useEnquiry();
  return (
    <header className="sticky top-0 z-50 bg-[#14263D]/95 backdrop-blur-xl border-b border-white/10 text-white" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-20 flex items-center justify-between gap-3 lg:gap-6">
        <Brand />
        <nav className="hidden xl:flex items-center gap-7 xl:gap-9 text-[13px] font-medium text-white/80" data-testid="site-nav">
          <ExpertiseMenu />
          {NAV.map((n) => (
            n.to.includes("#")
              ? <Link key={n.to} to={n.to} className="site-link py-1 hover:text-white" data-testid={`nav-${n.id}`}>{n.label}</Link>
              : <NavLink key={n.to} to={n.to} className={({ isActive }) => `site-link py-1 hover:text-white ${isActive ? "active text-white" : ""}`} data-testid={`nav-${n.id}`}>{n.label}</NavLink>
          ))}
        </nav>
        <div className="hidden xl:flex items-center gap-3">
          <Link to="/portal" className="px-4 h-10 inline-flex items-center gap-2 text-xs font-semibold tracking-wide border border-[#C4A66A]/40 text-[#C4A66A] hover:bg-[#C4A66A] hover:text-[#14263D] transition-colors duration-200" data-testid="nav-employee-login">
            <LogIn className="h-3.5 w-3.5" /> Employee Login
          </Link>
          <button onClick={() => openEnquiry()} className="btn-site-gold px-5 h-10 text-xs font-semibold tracking-wide" data-testid="nav-enquire-btn">Enquire now</button>
        </div>
        <div className="flex xl:hidden items-center gap-2">
          <Link to="/portal" className="h-9 px-2.5 whitespace-nowrap inline-flex items-center gap-1.5 text-[11px] font-semibold border border-[#C4A66A]/50 text-[#C4A66A]" data-testid="mobile-top-employee-login">
            <LogIn className="h-3.5 w-3.5 hidden sm:block" /> Employee Login
          </Link>
          <MobileMenu onEnquire={() => openEnquiry()} />
        </div>
      </div>
    </header>
  );
}
